import {
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  WithStyles,
  withStyles,
} from "@material-ui/core";
import Button from "@components/common/button/Button";
import FullScreenCard from "@components/common/fullscreencard/FullScreenCard";
import VarannanVeckaLogo from "@components/svg/varannanvecka";
import * as UserActions from "@services/user/user.actions";
import {
  confirmGmailLinkAttempt,
  inspectGmailLinkAttempt,
} from "@services/gmailIntegration/gmailIntegration.http";
import {
  IGmailInspectResponse,
  IGmailTarget,
} from "@services/gmailIntegration/gmailIntegration.types";
import { classifyGmailIntegrationError } from "@services/gmailIntegration/gmailIntegration.errors";
import TranslateNew from "@translate/TranslateNew";
import * as React from "react";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import {
  captureGmailAttemptFromCurrentUrl,
  clearPendingGmailAttempt,
  getPendingGmailAttempt,
} from "../../../integrations/gmail/gmailAttemptVault";
import { gmailConnectLoginState } from "../../../router/safeReturn";
import { GmailConnectPageStyle as styles } from "./GmailConnectPage.style";

const tn = TranslateNew.getInstance();

type GmailConnectView =
  | "loading"
  | "ready"
  | "submitting"
  | "success"
  | "cancelled"
  | "expired"
  | "conflict"
  | "no_family"
  | "offline"
  | "server"
  | "invalid";

interface IGmailConnectState {
  view: GmailConnectView;
  googleEmail: string;
  targets: IGmailTarget[];
  selectedTargetId: string;
  connectedTargetName: string;
}

interface IInjectedProps {
  authToken?: string;
  canOpenFamilySetup?: boolean;
  onSessionExpired: () => void;
  inspectAttempt?: (attempt: string, authToken: string) => Promise<IGmailInspectResponse>;
  confirmAttempt?: (attempt: string, targetId: string, authToken: string) => Promise<void>;
}

export type GmailConnectPageProps = IInjectedProps
  & RouteComponentProps
  & WithStyles<typeof styles>;

export class GmailConnectPageComponent extends React.Component<GmailConnectPageProps, IGmailConnectState> {
  public state: IGmailConnectState = {
    view: "loading",
    googleEmail: "",
    targets: [],
    selectedTargetId: "",
    connectedTargetName: "",
  };

  private attempt?: string;
  private confirmationInFlight = false;
  private mounted = false;
  private preserveAttemptOnUnmount = false;
  private headingRef = React.createRef<HTMLHeadingElement>();
  private previousTitle = "";

  public componentDidMount() {
    this.mounted = true;
    this.previousTitle = document.title;
    document.title = `${tn.lang.gmailConnectHeading} | Co-parenting`;

    captureGmailAttemptFromCurrentUrl();
    this.attempt = getPendingGmailAttempt();

    if (!this.props.authToken) {
      if (!this.attempt) {
        this.moveTo("invalid");
        return;
      }
      this.preserveAttemptOnUnmount = true;
      this.props.history.replace("/login", gmailConnectLoginState());
      return;
    }

    if (!this.attempt) {
      this.moveTo("invalid");
      return;
    }

    this.inspect();
  }

  public componentWillUnmount() {
    this.mounted = false;
    if (!this.preserveAttemptOnUnmount) {
      clearPendingGmailAttempt();
    }
    document.title = this.previousTitle;
  }

  public componentDidUpdate(previousProps: GmailConnectPageProps) {
    if (previousProps.authToken && !this.props.authToken) {
      // PublicRouter performs the actual safe redirect. Mark the handoff before
      // this component unmounts so a session timeout cannot discard the link.
      this.preserveAttemptOnUnmount = true;
    }
  }

  public render() {
    return (
      <FullScreenCard sideElement={<VarannanVeckaLogo />}>
        <main className={this.props.classes.content} aria-busy={this.state.view === "loading" || this.state.view === "submitting"}>
          {this.renderContent()}
        </main>
      </FullScreenCard>
    );
  }

  private renderContent(): React.ReactNode {
    const { classes } = this.props;
    const { view } = this.state;

    if (view === "loading") {
      return (
        <div className={classes.loading} role="status" aria-live="polite" aria-atomic="true">
          <CircularProgress size={32} aria-hidden="true" />
          <p className={classes.text}>{tn.lang.gmailCheckingLink}</p>
        </div>
      );
    }

    if (view === "success") {
      return (
        <>
          {this.heading(tn.lang.gmailConnectedHeading)}
          <p className={classes.text} role="status" aria-live="polite">
            {tn.lang.gmailConnectedText(this.state.googleEmail, this.state.connectedTargetName)}
          </p>
          <Button size="large" variant="contained" color="primary" onClick={this.handleCloseClick}>
            {tn.lang.gmailReturnToGmail}
          </Button>
          <p className={classes.text}>{tn.lang.gmailCloseFallback}</p>
        </>
      );
    }

    if (view === "cancelled") {
      return (
        <>
          {this.heading(tn.lang.gmailCancelledHeading)}
          <p className={classes.text} role="status" aria-live="polite">{tn.lang.gmailCancelledText}</p>
          <Button size="large" variant="contained" color="primary" onClick={this.handleCloseClick}>
            {tn.lang.close}
          </Button>
          <p className={classes.text}>{tn.lang.gmailCloseFallback}</p>
        </>
      );
    }

    if (view === "ready" || view === "submitting") {
      return this.renderConfirmation();
    }

    return this.renderError(view);
  }

  private renderConfirmation(): React.ReactNode {
    const { classes } = this.props;
    const isMultiple = this.state.targets.length > 1;
    const onlyTarget = this.state.targets.length === 1 ? this.state.targets[0] : undefined;
    const submitting = this.state.view === "submitting";

    return (
      <>
        {this.heading(tn.lang.gmailConnectHeading)}
        {onlyTarget && (
          <p className={classes.text} id="gmail-connect-question">
            {tn.lang.gmailConnectQuestion(this.state.googleEmail, onlyTarget.displayName)}
          </p>
        )}
        {isMultiple && (
          <>
            <p className={classes.text}>{this.state.googleEmail}</p>
            <p className={classes.text}>{tn.lang.gmailChooseFamily}</p>
          </>
        )}
        <p className={classes.text} id="gmail-connect-explanation">{tn.lang.gmailConnectExplanation}</p>

        <form className={classes.form} onSubmit={this.handleConfirm} aria-describedby="gmail-connect-explanation">
          {isMultiple && (
            <fieldset className={classes.choices}>
              <legend className={classes.legend}>{tn.lang.gmailFamilyLegend}</legend>
              <RadioGroup
                name="gmail-target"
                value={this.state.selectedTargetId}
                onChange={this.handleTargetChange}
              >
                {this.state.targets.map((target) => (
                  <FormControlLabel
                    className={classes.radioLabel}
                    key={target.id}
                    value={target.id}
                    control={<Radio color="primary" inputProps={{ "aria-label": target.displayName }} />}
                    label={target.displayName}
                    disabled={submitting}
                  />
                ))}
              </RadioGroup>
            </fieldset>
          )}

          <div className={classes.actions}>
            <Button
              size="large"
              variant="contained"
              color="primary"
              type="submit"
              disabled={submitting || !this.state.selectedTargetId}
            >
              {submitting ? tn.lang.connecting : tn.lang.gmailConnectButton}
            </Button>
            <Button
              size="large"
              variant="outlined"
              color="primary"
              type="button"
              disabled={submitting}
              onClick={this.handleCancel}
            >
              {tn.lang.cancel}
            </Button>
          </div>
          {submitting && (
            <span className={classes.visuallyHidden} role="status" aria-live="polite">
              {tn.lang.connecting}
            </span>
          )}
        </form>
      </>
    );
  }

  private renderError(view: GmailConnectView): React.ReactNode {
    const { classes } = this.props;
    const familySetupLinkProps: any = {
      href: "/app/settings/:userId",
      target: "_blank",
      rel: "noopener noreferrer",
    };
    let message = tn.lang.gmailServerError;
    let canRetry = false;
    let familySetup = false;

    if (view === "expired" || view === "invalid") {
      message = tn.lang.gmailLinkExpired;
    } else if (view === "conflict") {
      message = tn.lang.gmailAccountConflict;
    } else if (view === "no_family") {
      message = tn.lang.gmailNoAuthorizedFamilies;
      // Family settings needs an existing family WebSocket. Do not send a
      // genuine zero-resource account into PrivateRouter's loading gate; the
      // guidance remains visible and the attempt can still be retried here.
      familySetup = this.props.canOpenFamilySetup === true;
      canRetry = true;
    } else if (view === "offline") {
      message = tn.lang.gmailOfflineError;
      canRetry = true;
    } else if (view === "server") {
      canRetry = true;
    }

    return (
      <>
        {this.heading(tn.lang.gmailConnectHeading)}
        <div className={classes.notice} role="alert" aria-live="assertive">
          <p className={classes.text}>{message}</p>
        </div>
        <div className={classes.actions}>
          {familySetup && (
            <Button
              {...familySetupLinkProps}
              size="large"
              variant="contained"
              color="primary"
            >
              {tn.lang.gmailOpenFamilySetup}
            </Button>
          )}
          {canRetry && (
            <Button
              size="large"
              variant={familySetup ? "outlined" : "contained"}
              color="primary"
              onClick={this.inspect}
            >
              {tn.lang.tryAgain}
            </Button>
          )}
          <Button size="large" variant="outlined" color="primary" onClick={this.handleCancel}>
            {tn.lang.cancel}
          </Button>
        </div>
      </>
    );
  }

  private heading(text: string): React.ReactNode {
    return (
      <h1 className={this.props.classes.heading} ref={this.headingRef} tabIndex={-1}>
        {text}
      </h1>
    );
  }

  private inspect = async () => {
    if (!this.attempt || !this.props.authToken) {
      this.moveTo("invalid");
      return;
    }

    if (this.mounted) {
      this.setState({ view: "loading" });
    }

    try {
      const inspect = this.props.inspectAttempt || inspectGmailLinkAttempt;
      const result = await inspect(this.attempt, this.props.authToken);
      if (!this.mounted) return;

      const targets = this.validTargets(result);
      const expiry = Date.parse(result && result.expiresAt);

      if (!result || typeof result.googleEmail !== "string" || !result.googleEmail.trim()
        || !isFinite(expiry) || expiry <= Date.now()) {
        this.moveTo("expired");
        return;
      }
      if (targets.length === 0) {
        this.moveTo("no_family", { googleEmail: result.googleEmail, targets: [] });
        return;
      }

      const suggested = typeof result.suggestedTargetId === "string"
        && targets.some((target) => target.id === result.suggestedTargetId)
        ? result.suggestedTargetId
        : "";
      this.setState({
        view: "ready",
        googleEmail: result.googleEmail,
        targets,
        selectedTargetId: targets.length === 1 ? targets[0].id : suggested,
      });
    } catch (error) {
      if (!this.mounted) return;
      this.handleRequestError(error);
    }
  };

  private handleConfirm = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (this.confirmationInFlight || !this.attempt || !this.props.authToken) return;

    const selectedTarget = this.state.targets.find((target) => target.id === this.state.selectedTargetId);
    if (!selectedTarget) return;

    this.confirmationInFlight = true;
    this.setState({ view: "submitting" });

    try {
      const confirm = this.props.confirmAttempt || confirmGmailLinkAttempt;
      await confirm(this.attempt, selectedTarget.id, this.props.authToken);
      if (!this.mounted) return;

      clearPendingGmailAttempt();
      this.setState({
        view: "success",
        connectedTargetName: selectedTarget.displayName,
      }, this.focusHeading);
    } catch (error) {
      if (!this.mounted) return;
      this.confirmationInFlight = false;
      this.handleRequestError(error);
    }
  };

  private handleRequestError(error: any) {
    const errorType = classifyGmailIntegrationError(error);
    if (errorType === "unauthorized") {
      // Keep rendering this route until logout has removed the rejected token.
      // PublicRouter owns the subsequent auth transition to /login. Navigating
      // first would briefly mount LoginContainer with the stale token, which
      // immediately redirects back here and can start a 401 loop.
      this.preserveAttemptOnUnmount = true;
      this.props.onSessionExpired();
      return;
    }

    const view: GmailConnectView = errorType === "generic" ? "server" : errorType;
    this.moveTo(view);
  }

  private moveTo(view: GmailConnectView, state: Partial<IGmailConnectState> = {}) {
    if (!this.mounted) return;
    this.setState({ ...state, view } as Pick<IGmailConnectState, keyof IGmailConnectState>, this.focusHeading);
  }

  private validTargets(result: IGmailInspectResponse): IGmailTarget[] {
    if (!result || !Array.isArray(result.targets)) return [];
    const ids: { [id: string]: boolean } = {};

    return result.targets.filter((target) => {
      if (!target || typeof target.id !== "string" || !target.id
        || typeof target.displayName !== "string" || !target.displayName || ids[target.id]) {
        return false;
      }
      ids[target.id] = true;
      return true;
    });
  }

  private handleTargetChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const targetId = event.target.value;
    if (this.state.targets.some((target) => target.id === targetId)) {
      this.setState({ selectedTargetId: targetId });
    }
  };

  private handleCancel = () => {
    clearPendingGmailAttempt();
    this.setState({ view: "cancelled" }, this.focusHeading);
    window.close();
  };

  private handleCloseClick = () => {
    window.close();
  };

  private focusHeading = () => {
    if (this.headingRef.current) {
      this.headingRef.current.focus();
    }
  };
}

function mapStateToProps(state: any) {
  const sessionData = state && state.user && state.user.session && state.user.session.data
    ? state.user.session.data
    : {};
  const authorization = sessionData.authorization;
  return {
    authToken: typeof authorization === "string" && authorization.length > 0
      ? authorization
      : undefined,
    canOpenFamilySetup: Array.isArray(sessionData.resources) && sessionData.resources.length > 0,
  };
}

const mapDispatchToProps = {
  onSessionExpired: UserActions.logout,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(GmailConnectPageComponent)));



// WEBPACK FOOTER //
// ./src/components/integrations/gmail/GmailConnectPage.tsx